import { nativeDocument } from "../shared/document-session";
import { useState } from "react";
import type { WorkspaceModel } from "./useWorkspace";
import { tidyGrid } from "./sheet-operations";
import { notify } from "./api";
export function ArrangeControls({ model }: { model: WorkspaceModel }) {
  const { editor, selected } = model;
  const [aspect, setAspect] = useState(true);
  if (
    !model.canEdit ||
    !selected.length ||
    selected.some((s) => editor.isShapeOrAncestorLocked(s))
  )
    return null;
  const same =
    selected.length > 1 &&
    selected.every(
      (s) =>
        s.parentId === selected[0].parentId &&
        !["sheet", "trace", "mark"].includes(s.type) &&
        !nativeDocument(s),
    );
  const single = selected.length === 1 ? selected[0] : undefined;
  const bounds = single ? editor.getShapeGeometry(single).bounds : undefined;
  const run = (fn: () => void) => {
    try {
      editor.markHistoryStoppingPoint("Arrange");
      fn();
    } catch (e) {
      notify((e as Error).message);
    }
  };
  return (
    <details className="arrange-controls">
      <summary>Arrange & size</summary>
      {same && (
        <>
          <button
            onClick={() =>
              run(() => editor.groupShapes(editor.getSelectedShapeIds()))
            }
          >
            Group
          </button>
          <div className="arrange-grid">
            {(
              [
                "left",
                "center-horizontal",
                "right",
                "top",
                "center-vertical",
                "bottom",
              ] as const
            ).map((v) => (
              <button
                key={v}
                onClick={() =>
                  run(() => editor.alignShapes(editor.getSelectedShapeIds(), v))
                }
              >
                Align {v.replace("center-", "")}
              </button>
            ))}
          </div>
          {(["horizontal", "vertical"] as const).map((v) => (
            <button
              key={v}
              disabled={selected.length < 3}
              onClick={() =>
                run(() =>
                  editor.distributeShapes(editor.getSelectedShapeIds(), v),
                )
              }
            >
              Distribute {v}
            </button>
          ))}
          <button onClick={() => run(() => tidyGrid(editor))}>Tidy grid</button>
        </>
      )}
      {single?.type === "group" && (
        <button onClick={() => run(() => editor.ungroupShapes([single.id]))}>
          Ungroup
        </button>
      )}
      {single &&
        !["sheet", "trace", "mark"].includes(single.type) &&
        bounds && (
          <>
            <label>
              Rotation (°)
              <input
                aria-label="Object rotation"
                type="number"
                defaultValue={Math.round((single.rotation * 180) / Math.PI)}
                key={`${single.id}:${single.rotation}`}
                onBlur={(e) => {
                  if (
                    e.target.value !== "" &&
                    Number.isFinite(e.target.valueAsNumber)
                  )
                    run(() =>
                      editor.updateShape({
                        id: single.id,
                        type: single.type,
                        rotation: (e.target.valueAsNumber * Math.PI) / 180,
                      }),
                    );
                }}
              />
            </label>
            {["w", "h"].map((axis) => (
              <label key={axis}>
                {axis === "w" ? "Width" : "Height"} (canvas units)
                <input
                  aria-label={axis === "w" ? "Object width" : "Object height"}
                  type="number"
                  min="1"
                  key={`${single.id}:${bounds[axis as "w" | "h"]}`}
                  defaultValue={Math.round(bounds[axis as "w" | "h"])}
                  onBlur={(e) => {
                    const n = e.target.valueAsNumber;
                    if (n > 0 && Number.isFinite(n)) {
                      const scale = n / bounds[axis as "w" | "h"];
                      run(() =>
                        editor.resizeShape(single.id, {
                          x:
                            aspect || single.type === "image" || axis === "w"
                              ? scale
                              : 1,
                          y:
                            aspect || single.type === "image" || axis === "h"
                              ? scale
                              : 1,
                        }),
                      );
                    }
                  }}
                />
              </label>
            ))}
            <label>
              <input
                type="checkbox"
                checked={aspect || single.type === "image"}
                disabled={single.type === "image"}
                onChange={(e) => setAspect(e.target.checked)}
              />
              Keep proportions
            </label>
            <small>
              Image dimensions preserve proportions and source calibration
              rules.
            </small>
          </>
        )}
    </details>
  );
}
